import React, { useState, useContext, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import Context from "../ProductsProvider";

//style rules
import { Colors, Typography, Sizes } from "../../../styles";

const DropDown = ({ dropDownFilters, filterKey}) => {
	const {
		actions: {setParams, setLabel,},
		data: {language, label},
	} = useContext(Context);

	const [openFiltersDropDown, setOpenFiltersDropDown] = useState(false);

	const dropDownNode = useRef();

	const handleOutsideClick = (e) => {
		if (dropDownNode.current && !dropDownNode.current.contains(e.target)) {
			toggleDropDown();
		}
	};

	useEffect(() => {
		if (openFiltersDropDown) {
			document.addEventListener("mousedown", handleOutsideClick);
		}
		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
		};
	}, [openFiltersDropDown]);

	const toggleDropDown = () => {
		setOpenFiltersDropDown((currentValue) => !currentValue);
	};

	// grabs the url search params object and sets the active sort filter
	useEffect(() => {
		const url = new URL(window.location.href);

		for (const [key, value] of url.searchParams.entries()) {
			if (key === filterKey) {
				setLabel(() => {
					const filter = dropDownFilters.find(filter => filter.slug === value);
					return filter.title;
				});
			}
		}
	}, []);

	const sortByText = (language) => {
		if (language === "en") return "Sort by:";
		return "Trier par:";
	}

	const handleFiltersSelect = (filterObject, filterTitle) => {
		const label = filterObject.sort === 'dropdown_label';

		if (label) {	
			toggleDropDown();
			return;
		}

		setParams((currentValue) => {
			const searchParams = new URLSearchParams(currentValue.toString());
			searchParams.set(filterKey, filterObject.sort);
			return searchParams;
		});
		setLabel(filterTitle);
		toggleDropDown();
	}

	const handleKeyUp = (event) => {
		if (event.key === "Enter") {
			toggleDropDown();
		}
	};

	const renderFilters = () => {
		// we only run the function when the dropdown is triggered
		if (!openFiltersDropDown) return;
		const newDropdownFilters = [
			{slug: 'dropdown_label', title: `${sortByText(language)} ${label}`},
			...dropDownFilters]

		return (
			<DropDownList 
				role="listbox"
				id="dropdown-list"
				aria-hidden={!openFiltersDropDown}
			>
				{newDropdownFilters &&
					newDropdownFilters.length > 0 &&
					newDropdownFilters.map((filter) => {
						return (
							<DropDownListItem
								key={filter.slug}
								onClick={() => {
									handleFiltersSelect({[filterKey]: filter.slug}, filter.title);
								}}
								tabIndex='0'
								className={filter.slug === 'dropdown_label' ? 'dropdown-label' : null}
								role="option"
								aria-selected={label === filter.title}
								language={language}
							>
								{
									filter.slug === 'dropdown_label' ?
										<>
											<ItemLabel>
												{`${sortByText(language)} ${label}`}
											</ItemLabel>
											<img src={`${mainScript.assetPath}images/svg/icons/down-arrow-black.svg`} alt="" />
										</>
										: <ItemLabel
											dangerouslySetInnerHTML={{ __html: filter.title }}
										/>

								}
							</DropDownListItem>
						);
					})}
			</DropDownList>
		);
	};

	return (
		<DropDownContainer 
			ref={dropDownNode} 
			className={openFiltersDropDown ? 'open' : null}
		>
			<DropDownTrigger 
				onClick={toggleDropDown} 
				onKeyUp={handleKeyUp} 
				language={language} 
				aria-controls={openFiltersDropDown ? "dropdown-list" : ""}
				aria-expanded={openFiltersDropDown}
			>
				{`${sortByText(language)} ${label}`}
				<img src={`${mainScript.assetPath}images/svg/icons/down-arrow-black.svg`} alt="" />
			</DropDownTrigger>
			{renderFilters()}
		</DropDownContainer>
	);
};

export default DropDown;

DropDown.propTypes = {
	DropDownLabel: PropTypes.string,
	dropDownFilters: PropTypes.array,
	filterKey: PropTypes.string,
	filterIndex: PropTypes.number,
};

const DropDownTrigger = styled.button`
	border-radius: unset;
	color: ${Colors.base.black};
	font-size: ${props => props.language !== "en" ? "1.4rem" : "1.5rem"};
	line-height: ${props => props.language !== "en" ? "1.7rem" : "1.8rem"};
	font-weight: ${Typography.fontWeight.bold};
	letter-spacing: -0.015rem;
	display: flex;
	flex-wrap: nowrap;
	column-gap: 0.7rem;
	background-color: ${Colors.fp.pineGreen40};
	padding: 2.4rem 1rem;
	align-items: center;
	justify-content: center;
	width: 100%;
	
	@media only screen and (min-width: ${Sizes.screens.large}) {
		text-align: left;
		font-size: 1.4rem;
		font-weight: ${Typography.fontWeight.medium};
		line-height: 1.9rem;
		padding: 0.5rem 1rem;
		border-radius: 0.5rem;
		border: 1px solid ${Colors.fp.pineGreen};
		background-color: ${Colors.fp.pineGreen10};
		justify-content: space-between;
	}

	img {
		position: relative;

		@media only screen and (min-width: ${Sizes.screens.large}) {
			width: 1.5rem;
		}
	}

	&:hover,
	&:focus {
		@media only screen and (min-width: ${Sizes.screens.large}) {
			padding-right: 1rem;
		}

		&::after {
			@media only screen and (min-width: ${Sizes.screens.large}) {
				content: none;
			}
		}
	}


	&:focus-visible {
		@media only screen and (min-width: ${Sizes.screens.large}) {
			outline: solid 2px -webkit-focus-ring-color;
			outline: solid 2px Highlight;
		}
	}    
`;

const DropDownContainer = styled.div`
    margin: 0;
	position: relative;

	&.open {
		& > ${DropDownTrigger} {
			img {
				transform: rotate(180deg) !important;
				top: 1.5rem !important;
			}
		}
	}
`;
    
const DropDownList = styled.ul`
	display: flex;
	flex-direction: column;
	margin: 0;
	padding: 0;
	position: absolute;
	top: 0;
	left: 0;
	z-index: 3;
	background-color: ${Colors.fp.pineGreen10};
	width: 100%;
`;

const ItemLabel = styled.label`
	font-size: 15px;
	line-height: 1.9rem;
	font-weight: ${Typography.fontWeight.medium};
	cursor: pointer;
	text-transform: unset;
	letter-spacing: normal;
`;

const DropDownListItem = styled.li`
	margin: 0;
	padding: 0.5rem 1rem;
	list-style: none;
	cursor: pointer;

	&:hover,
	&:focus-visible {
		@media only screen and (min-width: ${Sizes.screens.large}) {
			background-color: ${Colors.fp.pineGreen40};
		}
	}

	label::first-letter {
		text-transform: uppercase;
	}

	&.dropdown-label {
		background-color: ${Colors.fp.pineGreen40};
		display: flex;
		flex-wrap: nowrap;
		column-gap: 0.7rem;
		padding: 2.4rem 1rem;
		align-items: center;
		letter-spacing: -0.015rem;
		justify-content: center !important;
		
		@media only screen and (min-width: ${Sizes.screens.large}) {
			justify-content: space-between !important;
			padding: 0.5rem 1rem;
			border-radius: 0.5rem;
			border: 1px solid ${Colors.fp.pineGreen};
			background-color: ${Colors.fp.pineGreen10};
			line-height: 1.9rem;
		}

		img {
			transform: rotate(180deg);
			position: relative;

			@media only screen and (min-width: ${Sizes.screens.large}) {
				width: 1.5rem;
			}
		}
	}

	&.dropdown-label ${ItemLabel} {
		pointer-events: none;
		cursor: not-allowed;
		font-weight: ${Typography.fontWeight.bold};
		font-size: ${props => props.language !== "en" ? "1.4rem" : "1.5rem"};
		line-height: ${props => props.language !== "en" ? "1.7rem" : "1.8rem"};
		letter-spacing: -0.015rem;
		position: relative;
		background-color: ${Colors.fp.pineGreen40};
		
		@media only screen and (min-width: ${Sizes.screens.large}) {
			text-align: left;
			font-size: 1.4rem;
			line-height: 1.9rem;
			background-color: ${Colors.fp.pineGreen10};
			font-weight: ${Typography.fontWeight.medium};
		}
	}
`;
